import React from 'react';
import {
  MapPin, Globe, Mail, Phone, Star, Award, Clock, GraduationCap,
  FileText, User, CheckCircle, Briefcase, ArrowUpRight, ShieldCheck, FileCheck,
} from 'lucide-react';
import { fileUrl } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import { allCountries } from '../../data/quoteFormOptions';
import './TranslatorProfileView.css';

function chip(label, key) {
  return (
    <span className="tp-chip" key={key}>
      {label}
    </span>
  );
}

function fileName(path) {
  if (!path) return '';
  return String(path).split('/').pop() || String(path);
}

const WORK_MODE_LABELS = {
  remote: 'À distance',
  onsite: 'Sur site',
  both: 'Les deux',
};

const AVAILABILITY_LABELS = {
  disponible: 'Disponible',
  partiellement_disponible: 'Partiellement disponible',
  indisponible: 'Indisponible',
  sur_demande: 'Sur demande',
  en_mission: 'En mission',
};

const AVAILABILITY_OPTION_LABELS = {
  immediat: 'Disponible immédiatement',
  sur_demande: 'Disponible sur demande',
  indisponible_temporaire: 'Indisponible temporairement',
};

const DELAY_LABELS = {
  standard: 'Standard',
  rapide: 'Rapide',
  urgent: 'Urgent',
};

const COUNTRY_NAMES = { TN: 'Tunisie', FR: 'France', DZ: 'Algérie', MA: 'Maroc' };

export default function TranslatorProfileView({ user }) {
  const { t } = useLanguage();
  const a = t.dashboard?.admin || {};
  const p = user?.translatorProfile || {};
  const civility = p?.civility || user?.civility || '';
  const baseName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.name || a.unknown || 'Traducteur';
  const name = civility ? `${civility} ${baseName}` : baseName;
  const photo = fileUrl(user?.photoUrl || p?.photoUrl);

  const displayLanguages = (p?.languageCombos?.length ? p.languageCombos : user?.languageCombos) || [];
  const displaySpecialties = (p?.specialties?.length ? p.specialties : user?.specialties) || [];
  const displayTranslationServices = (p?.translationServices?.length ? p.translationServices : user?.translationServices) || [];
  const displayDocumentTypes = (p?.documentTypes?.length ? p.documentTypes : user?.documentTypes) || [];
  const displayOfficialServices = (p?.officialServices?.length ? p.officialServices : user?.officialServices) || [];
  const displayInterpretingServices = (p?.interpretingServices?.length ? p.interpretingServices : user?.interpretingServices) || [];
  const displayMissionTypes = (p?.interpretingMissionTypes?.length ? p.interpretingMissionTypes : user?.interpretingMissionTypes) || [];
  const displayInterpretingModes = (p?.interpretingModes?.length ? p.interpretingModes : user?.interpretingModes) || [];
  const displaySkills = (p?.skills?.length ? p.skills : user?.skills) || [];
  const countryCode = p?.country || user?.country || '';
  const countryLabel = COUNTRY_NAMES[countryCode] || allCountries.find((c) => c.code === countryCode)?.name || countryCode;
  const displayTools = (p?.tools?.length ? p.tools : user?.tools) || [];
  const displayExpertise = (p?.expertiseAreas?.length ? p.expertiseAreas : user?.expertiseAreas) || [];

  const typeLabel =
    p?.professionalType === 'translator' ? 'Traducteur'
    : p?.professionalType === 'interpreter' ? 'Interprète'
    : 'Traducteur & Interprète';

  const rateRows = [
    { label: a.pageRate || 'Tarif page', value: p?.pageRate ? `${p.pageRate} ${p?.ratesCurrency || 'TND'}` : null },
    { label: a.hourlyRate || 'Tarif heure', value: p?.hourlyRate ? `${p.hourlyRate} ${p?.ratesCurrency || 'TND'}` : null },
    ...((p?.rates || []).map((r) => ({
      label: r.service || a.rate || 'Tarif',
      value: [r.perWord ? `${r.perWord} TND/mot` : '', r.perPage ? `${r.perPage} TND/page` : '', r.minimum ? `(min ${r.minimum})` : ''].filter(Boolean).join(' '),
    }))),
  ].filter((x) => x.value);

  return (
    <div className="translator-profile">
      <aside className="tp-sidebar">
        <div className="tp-avatar">
          {photo ? (
            <img src={photo} alt={name} />
          ) : (
            <div className="tp-avatar-placeholder"><User size={56} strokeWidth={1.6} /></div>
          )}
        </div>

        <div className="tp-sidebar-block">
          <h3>Contact</h3>
          <div className="tp-list">
            {p?.professionalEmail && (
              <a className="tp-list-item" href={`mailto:${p.professionalEmail}`}>
                <Mail size={14} /> {p.professionalEmail}
              </a>
            )}
            {!p?.professionalEmail && user?.email && (
              <a className="tp-list-item" href={`mailto:${user.email}`}>
                <Mail size={14} /> {user.email}
              </a>
            )}
            {user?.phone && (
              <a className="tp-list-item" href={`tel:${user.phone}`}>
                <Phone size={14} /> {user.phone}
              </a>
            )}
            {p?.website && (
              <a className="tp-list-item" href={p.website} target="_blank" rel="noreferrer">
                <Globe size={14} /> {p.website}
              </a>
            )}
            <div className="tp-list-item tp-list-item--muted">
              <MapPin size={14} />
              {[p?.address, p?.region || user?.region, user?.city, countryLabel].filter(Boolean).join(', ') || a.noLocation || 'Non renseigné'}
            </div>
          </div>
        </div>

        <div className="tp-sidebar-block">
          <h3>Langues</h3>
          <div className="tp-chips">
            {user?.motherTongue && chip(t.quoteForm?.languages?.[user.motherTongue] || user.motherTongue, 'mt')}
            {displayLanguages.map((c, i) =>
              chip(`${t.quoteForm?.languages?.[c.source] || c.source} → ${t.quoteForm?.languages?.[c.target] || c.target} (${c.level})`, `pair-${i}`)
            )}
          </div>
        </div>

        {displaySkills.length > 0 && (
          <div className="tp-sidebar-block">
            <h3>Compétences</h3>
            <div className="tp-chips">{displaySkills.map((s, i) => chip(s, `skill-${i}`))}</div>
          </div>
        )}

        {displayTools.length > 0 && (
          <div className="tp-sidebar-block">
            <h3>Outils</h3>
            <div className="tp-chips">{displayTools.map((s, i) => chip(s, `tool-${i}`))}</div>
          </div>
        )}

        {p?.cvUrl && (
          <a href={fileUrl(p.cvUrl)} target="_blank" rel="noreferrer" className="tp-cv">
            <FileText size={16} />
            {a.cv || 'Télécharger le CV'}
          </a>
        )}
      </aside>

      <main className="tp-main">
        <div className="tp-hero">
          <div>
            <h1 className="tp-name">{name}</h1>
            <p className="tp-role">{p?.professionalTitle || typeLabel}</p>
          </div>
          <div className="tp-badges">
            <span className={`tp-badge tp-badge--${p?.availabilityStatus === 'disponible' ? 'ok' : 'busy'}`}>
              {AVAILABILITY_LABELS[p?.availabilityStatus] || p?.availabilityStatus || 'Disponible'}
            </span>
            {(p?.sworn || p?.accredited || displayOfficialServices.includes('Traduction assermentée')) && (
              <span className="tp-badge tp-badge--gold">Assermenté</span>
            )}
          </div>
        </div>

        <div className="tp-grid">
          <section className="tp-card tp-card--wide">
            <header className="tp-card-head">
              <User size={16} />
              <h2>{a.bio || 'À propos'}</h2>
            </header>
            <p className="tp-bio">{p?.bio || a.notProvided || 'Aucune biographie.'}</p>
          </section>

          <section className="tp-card">
            <header className="tp-card-head">
              <CheckCircle size={16} />
              <h2>{a.identity || 'Identité'}</h2>
            </header>
            <div className="tp-meta">
              {civility && (
                <div>
                  <strong>{a.fieldCivility || 'Civilité'}</strong>
                  <span>{civility}</span>
                </div>
              )}
              <div>
                <strong>{a.professionalType || 'Type'}</strong>
                <span>{typeLabel}</span>
              </div>
              {p?.nationality && (
                <div>
                  <strong>{a.nationality || 'Nationalité'}</strong>
                  <span>{p.nationality}</span>
                </div>
              )}
              {user?.motherTongue && (
                <div>
                  <strong>{a.motherTongue || 'Langue maternelle'}</strong>
                  <span>{t.quoteForm?.languages?.[user.motherTongue] || user.motherTongue}</span>
                </div>
              )}
              <div>
                <strong>{a.experience || 'Expérience'}</strong>
                <span>{p?.experienceYears ? `${p.experienceYears} ans` : '—'}</span>
              </div>
              <div>
                <strong>{a.activityStartDate || 'Début activité'}</strong>
                <span>{p?.activityStartDate ? new Date(p.activityStartDate).toLocaleDateString('fr-FR') : '—'}</span>
              </div>
            </div>
          </section>

          <section className="tp-card">
            <header className="tp-card-head">
              <Clock size={16} />
              <h2>{a.availabilityAndRates || 'Disponibilité & tarifs'}</h2>
            </header>
            <div className="tp-meta">
              <div>
                <strong>{a.status || 'Statut'}</strong>
                <span>{AVAILABILITY_LABELS[p?.availabilityStatus] || p?.availabilityStatus || a.available || 'Disponible'}</span>
              </div>
              {(p?.availabilityOptions || []).length > 0 && (
                <div>
                  <strong>{a.availabilityOptions || 'Disponibilités'}</strong>
                  <span>{p.availabilityOptions.map((o) => AVAILABILITY_OPTION_LABELS[o] || o).join(', ')}</span>
                </div>
              )}
              {p?.availabilityNotes ? (
                <div>
                  <strong>{a.availabilityNotes || 'Jours et horaires'}</strong>
                  <span>{p.availabilityNotes}</span>
                </div>
              ) : null}
              <div>
                <strong>{a.availabilityType || 'Collaboration'}</strong>
                <span>{p?.availabilityType || 'Freelance'}</span>
              </div>
              <div>
                <strong>Mode de travail</strong>
                <span>{WORK_MODE_LABELS[p?.workMode] || p?.workMode || '—'}</span>
              </div>
              <div>
                <strong>Délai habituel</strong>
                <span>{DELAY_LABELS[p?.usualDelay] || p?.usualDelay || '—'}</span>
              </div>
              <div>
                <strong>Délais acceptés</strong>
                <span>{(p?.acceptedDelays || []).map((x) => DELAY_LABELS[x] || x).join(', ') || '—'}</span>
              </div>
              <div>
                <strong>{a.acceptsUrgent || 'Urgent'}</strong>
                <span>{p?.acceptsUrgent !== false ? 'Oui' : 'Non'}</span>
              </div>
              <div>
                <strong>{a.remoteWork || 'Télétravail'}</strong>
                <span>{p?.remoteWork !== false ? 'Oui' : 'Non'}</span>
              </div>
              <div>
                <strong>{a.acceptVerification || 'Vérification infos acceptée'}</strong>
                <span>{p?.acceptVerification ? 'Oui' : 'Non'}</span>
              </div>
              {rateRows.map((r, i) => (
                <div key={i}>
                  <strong>{r.label}</strong>
                  <span>{r.value}</span>
                </div>
              ))}
            </div>
          </section>

          {displayTranslationServices.length > 0 && (
            <section className="tp-card tp-card--wide">
              <header className="tp-card-head">
                <Award size={16} />
                <h2>{a.translationServices || 'Services de traduction'}</h2>
              </header>
              <div className="tp-chips">{displayTranslationServices.map((s, i) => chip(s, `ts-${i}`))}</div>
            </section>
          )}

          {displayDocumentTypes.length > 0 && (
            <section className="tp-card tp-card--wide">
              <header className="tp-card-head">
                <FileCheck size={16} />
                <h2>Types de documents</h2>
              </header>
              <div className="tp-chips">{displayDocumentTypes.map((s, i) => chip(s, `dt-${i}`))}</div>
            </section>
          )}

          {displayOfficialServices.length > 0 && (
            <section className="tp-card tp-card--wide">
              <header className="tp-card-head">
                <ShieldCheck size={16} />
                <h2>Services officiels</h2>
              </header>
              <div className="tp-chips">{displayOfficialServices.map((s, i) => chip(s, `os-${i}`))}</div>
              {(p?.accreditationCountry || p?.accreditationBody || p?.accreditationNumber || p?.accreditationJustificationUrl) && (
                <div className="tp-meta" style={{ marginTop: 10 }}>
                  {p?.accreditationCountry && (
                    <div>
                      <strong>Pays d&apos;habilitation</strong>
                      <span>{COUNTRY_NAMES[p.accreditationCountry] || p.accreditationCountry}</span>
                    </div>
                  )}
                  {p?.accreditationBody && (
                    <div>
                      <strong>Autorité / organisme</strong>
                      <span>{p.accreditationBody}</span>
                    </div>
                  )}
                  {p?.accreditationNumber && (
                    <div>
                      <strong>N° d&apos;habilitation</strong>
                      <span>{p.accreditationNumber}</span>
                    </div>
                  )}
                  {p?.accreditationJustificationUrl && (
                    <div>
                      <strong>Justificatif</strong>
                      <a className="tp-doc" href={fileUrl(p.accreditationJustificationUrl)} target="_blank" rel="noreferrer">
                        <ArrowUpRight size={14} /> {fileName(p.accreditationJustificationUrl)}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {displayInterpretingServices.length > 0 && (
            <section className="tp-card tp-card--wide">
              <header className="tp-card-head">
                <Award size={16} />
                <h2>{a.interpretingServices || 'Services d\'interprétation'}</h2>
              </header>
              <div className="tp-chips">{displayInterpretingServices.map((s, i) => chip(s, `is-${i}`))}</div>
              {(displayMissionTypes.length > 0 || displayInterpretingModes.length > 0) && (
                <div className="tp-meta" style={{ marginTop: 10 }}>
                  {displayMissionTypes.length > 0 && (
                    <div>
                      <strong>Types de missions</strong>
                      <span>{displayMissionTypes.join(', ')}</span>
                    </div>
                  )}
                  {displayInterpretingModes.length > 0 && (
                    <div>
                      <strong>Modes de prestation</strong>
                      <span>{displayInterpretingModes.join(', ')}</span>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {displaySpecialties.length > 0 && (
            <section className="tp-card tp-card--wide">
              <header className="tp-card-head">
                <Star size={16} />
                <h2>{a.specialties || 'Spécialisations'}</h2>
              </header>
              <div className="tp-chips">{displaySpecialties.map((s, i) => chip(s, `spec-${i}`))}</div>
            </section>
          )}

          {displayExpertise.length > 0 && (
            <section className="tp-card tp-card--wide">
              <header className="tp-card-head">
                <Briefcase size={16} />
                <h2>{a.expertiseAreas || 'Domaines d\'expertise'}</h2>
              </header>
              <div className="tp-chips">{displayExpertise.map((s, i) => chip(s, `exp-${i}`))}</div>
            </section>
          )}

          <section className="tp-card tp-card--wide">
            <header className="tp-card-head">
              <GraduationCap size={16} />
              <h2>{a.educationAndDocs || 'Formation & documents'}</h2>
            </header>
            <div className="tp-docs">
              {p?.diplomas?.length > 0 && (
                <>
                  <h4 className="tp-subtitle-label">{a.diplomas || 'Diplômes'}</h4>
                  {p.diplomas.map((d, i) => (
                    <a className="tp-doc" href={fileUrl(d) || '#'} target="_blank" rel="noreferrer" key={`d-${i}`}>
                      <FileText size={14} /> {fileName(d)}
                    </a>
                  ))}
                </>
              )}
              {p?.attestations?.length > 0 && (
                <>
                  <h4 className="tp-subtitle-label">{a.attestations || 'Attestations d\'expérience'}</h4>
                  {p.attestations.map((d, i) => (
                    <a className="tp-doc" href={fileUrl(d) || '#'} target="_blank" rel="noreferrer" key={`at-${i}`}>
                      <FileText size={14} /> {fileName(d)}
                    </a>
                  ))}
                </>
              )}
              {p?.otherDocuments?.length > 0 && (
                <>
                  <h4 className="tp-subtitle-label">{a.otherDocuments || 'Autres justificatifs'}</h4>
                  {p.otherDocuments.map((d, i) => (
                    <a className="tp-doc" href={fileUrl(d) || '#'} target="_blank" rel="noreferrer" key={`od-${i}`}>
                      <FileText size={14} /> {fileName(d)}
                    </a>
                  ))}
                </>
              )}
              {p?.certifications?.length > 0 && (
                <>
                  <h4 className="tp-subtitle-label">{a.certifications || 'Certifications'}</h4>
                  {p.certifications.map((c, i) => (
                    <a className="tp-doc" href={fileUrl(c) || '#'} target="_blank" rel="noreferrer" key={`c-${i}`}>
                      <FileText size={14} /> {fileName(c)}
                    </a>
                  ))}
                </>
              )}
              {p?.portfolio?.length > 0 && (
                <>
                  <h4 className="tp-subtitle-label">{a.portfolio || 'Portfolio'}</h4>
                  {p.portfolio.map((u, i) => (
                    <a className="tp-doc" href={u} target="_blank" rel="noreferrer" key={`p-${i}`}>
                      <ArrowUpRight size={14} /> {u}
                    </a>
                  ))}
                </>
              )}
              {!p?.diplomas?.length && !p?.certifications?.length && !p?.attestations?.length && !p?.otherDocuments?.length && !p?.portfolio?.length && !p?.cvUrl && (
                <p className="tp-empty">{a.notProvided || 'Aucun document renseigné.'}</p>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
